/**
 * AggregateOtherBucketBanner — muted inline hint shown below AI prose when an
 * `aggregate_panel_data` grouped result has `hasOtherBucket: true`.
 *
 * Visual treatment is identical to QueryPanelDataTruncationBanner (text-xs
 * text-secondary, divider above) per brand guide §36 and the Slice 3.2 brief —
 * aggregate grouped + otherBucket = prose + muted hint banner.
 *
 * Spec: TDD §3.7 / plans/2026-05-07-track-3-conversational-qa-tdd.md
 * Brand guide: §36 — muted prose hint consistent with other AI bubbles.
 * Era: dashboard-era only. Track 3 Slice 3.2.
 *
 * The banner is prose-only — no clickable CTA. The AI's `content` prose already
 * describes the breakdown; this banner adds the low-emphasis "other" signal so
 * the user knows there are additional uncollapsed groups beyond those listed.
 *
 * Accessibility:
 *  - `role="note"` + `aria-label` distinguishes the metadata from AI prose.
 *  - Same pattern as QueryPanelDataTruncationBanner and LibraryGapBanner.
 */

import React from 'react'
import type { AggregatePanelDataToolResult } from '@/types/Dashboards.types'

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AggregateOtherBucketBannerProps {
  /**
   * The aggregate tool result. The banner renders only when
   * `result.kind === 'grouped'` and `result.hasOtherBucket === true`.
   */
  qaToolResult: AggregatePanelDataToolResult
}

// ─── Component ────────────────────────────────────────────────────────────────

export const AggregateOtherBucketBanner: React.FC<AggregateOtherBucketBannerProps> = ({
  qaToolResult,
}) => {
  if (qaToolResult.result.kind !== 'grouped') return null
  if (!qaToolResult.result.hasOtherBucket) return null

  return (
    <div
      className="mt-3 pt-3 border-t border-border/60"
      role="note"
      aria-label="Aggregate result has additional groups"
      data-testid="aggregate-other-bucket-banner"
    >
      <p className="text-xs text-secondary leading-relaxed">
        Additional groups were collapsed as &ldquo;Other&rdquo; in this breakdown.
      </p>
    </div>
  )
}
